import { ref, get, child, update } from "firebase/database";
import { dbAsistencia } from "../firebase";

const STATUS = {
  PENDING: "pending",
  DENIED: "denied",
};

const REASON = {
  END_TIME_MISSING: "hora_fin_faltante",
};

function dmyFromDateInTZ(date, timeZone) {
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(date);

  const day = parts.find((p) => p.type === "day")?.value;
  const month = parts.find((p) => p.type === "month")?.value;
  const year = parts.find((p) => p.type === "year")?.value;
  return `${day}-${month}-${year}`;
}

function yesterdayDMY(timeZone = "America/Bogota") {
  const now = new Date();
  const d = new Date(now.getTime() - 24 * 60 * 60 * 1000);
  return dmyFromDateInTZ(d, timeZone);
}

function parseDMY(dmy) {
  const m = /^(\d{2})-(\d{2})-(\d{4})$/.exec(String(dmy || "").trim());
  if (!m) return null;
  const dd = Number(m[1]);
  const mm = Number(m[2]);
  const yyyy = Number(m[3]);
  if (!Number.isFinite(dd) || !Number.isFinite(mm) || !Number.isFinite(yyyy)) return null;
  const d = new Date(yyyy, mm - 1, dd);
  d.setHours(0, 0, 0, 0);
  return Number.isNaN(d.getTime()) ? null : d;
}

function normalizeType(typeRaw) {
  const t = String(typeRaw || "").trim().toLowerCase();
  const legacy = {
    al: "op",
    cl: "va",
    ml: "pe",
  };
  return legacy[t] || t;
}

function normalizeStatus(statusRaw) {
  const t = String(statusRaw || "").trim().toLowerCase();
  if (t === "approved" || t === "approve" || t === "aprobado") return "approved";
  if (t === "denied" || t === "rejected" || t === "reject" || t === "rechazado") return "denied";
  return STATUS.PENDING;
}

function parseRangeFromMessage(messageRaw) {
  const text = String(messageRaw || "").replace(/\s+/g, " ").trim();
  if (!text) return { ok: true, reason: null };

  const m = /desde\s*([0-9]{1,2}\s*:\s*[0-9]{1,2}(?:\s*[ap]\.?\s*m\.?)?)\s*hasta\s*((?:[0-9]{1,2}|--)\s*:\s*(?:[0-9]{1,2}|--)(?:\s*[ap]\.?\s*m\.?)?)/i.exec(text);
  if (!m) return { ok: true, reason: null };

  const parseTimeToken = (token, { allowMissing = false } = {}) => {
    const raw = String(token || "").trim().toLowerCase();
    const compact = raw.replace(/\s+/g, "");
    if (allowMissing && compact === "--:--") {
      return { missing: true };
    }

    const t = /^(\d{1,2}):(\d{1,2})(?:([ap])\.?m\.?)?$/.exec(compact);
    if (!t) return null;

    let hh = Number(t[1]);
    const mm = Number(t[2]);
    const mer = t[3] || null;

    if (!Number.isFinite(hh) || !Number.isFinite(mm) || mm < 0 || mm > 59) {
      return null;
    }

    if (mer) {
      if (hh < 1 || hh > 12) return null;
      if (mer === "a") hh = hh === 12 ? 0 : hh;
      if (mer === "p") hh = hh === 12 ? 12 : hh + 12;
    } else if (hh < 0 || hh > 23) {
      return null;
    }

    return { hh, mm, missing: false };
  };

  const fromParsed = parseTimeToken(m[1]);
  if (!fromParsed || fromParsed.missing) {
    return { ok: true, reason: null };
  }

  const toParsed = parseTimeToken(m[2], { allowMissing: true });
  if (!toParsed) return { ok: true, reason: null };
  if (toParsed.missing) {
    return { ok: false, reason: REASON.END_TIME_MISSING };
  }
  return { ok: true, reason: null };
}

export async function autoRejectInvalidServiceCommissionsFromClient({ timeZone = "America/Bogota" } = {}) {
  const root = ref(dbAsistencia);
  const leavesSnap = await get(child(root, "leaves"));
  const leaves = leavesSnap.exists() ? leavesSnap.val() : {};

  const yesterday = parseDMY(yesterdayDMY(timeZone));

  const updates = {};
  let reviewed = 0;
  let rejected = 0;

  Object.entries(leaves || {}).forEach(([userId, userLeaves]) => {
    Object.entries(userLeaves || {}).forEach(([leaveId, leave]) => {
      const type = normalizeType(leave?.type);
      const status = normalizeStatus(leave?.status);
      const appliedDate = String(leave?.appliedDate || "").trim();
      const fromDate = String(leave?.fromDate || "").trim();
      const baseDate = appliedDate || fromDate;
      const baseDateObj = parseDMY(baseDate);

      if (type !== "sc") return;
      if (status !== STATUS.PENDING) return;
      if (!baseDateObj || !yesterday || baseDateObj.getTime() > yesterday.getTime()) return;

      reviewed += 1;

      const check = parseRangeFromMessage(leave?.message);
      if (check.ok) return;

      const basePath = `leaves/${userId}/${leaveId}`;
      updates[`${basePath}/status`] = STATUS.DENIED;
      updates[`${basePath}/autoRejectedAt`] = Date.now();
      updates[`${basePath}/autoRejectedReason`] = check.reason;
      updates[`${basePath}/autoRejectedBy`] = "client_admin_panel";
      rejected += 1;
    });
  });

  if (Object.keys(updates).length) {
    await update(ref(dbAsistencia), updates);
  }

  return {
    targetDate: yesterdayDMY(timeZone),
    reviewed,
    rejected,
  };
}
